/**
 * Room controller
 *
 * Fetches room info and
 * provides events to components
 *
 * Events:
 *   - 'loading' (bool) - Loading status changed
 *   - 'room-roles'(room-id, {admin: bool, mod: bool, owner: bool}) - Room roles updated
 *   - 'room-meta'(room-id, { API RESPONSE }) - Room metadata updated. Null if no data yet
 *   - 'section-change' (mode, section.id) - Section changed (for display)
 *   - 'section-change-alt' (mode, section.id) - Alt Section changed (for display)
 *   - 'layout-change' (layout) - Layout mode changed
 *   Rest of room events (socket), except calls
 */

import { abortNamedApiRequest, Request } from "@asanrom/request-browser";
import { AppEvents } from "./app-events";
import { WebSocketController } from "./socket";
import { AuthController } from "./auth";
import { ApiRoom } from "@/api/api-group-room";
import { MultiStreamingItem } from "@/api/definitions";
import { translate } from "@/i18n";
import { CallsController } from "./call";
declare global {
    interface Window {
        FORCED_CALL_EXPAND?: boolean;
        ROOM_VERSION?: string;
        ROOM_VERSION_CODE?: string;
        USING_DARK_THEME?: boolean;
        ROOM_FORCED?: string;
    }
}

function $t(s: string): string {
    return translate(s);
}

export class RoomController {
    public static room: string = "";
    public static sectionMode: string = "section";
    public static section: string = "";
    public static sectionModeAlt: string = "section";
    public static sectionAlt: string = "";
    public static layout: string = "single";
    /** Room to switch the WebSocket to once the active call ends */
    public static pendingWebSocketRoom: string = "";
    public static isMultiStreaming: boolean = false;
    public static multiStreamingSelected: string = "";
    public static multiStreamingList: MultiStreamingItem[] = [];
    public static focus: string = window.FORCED_CALL_EXPAND ? "call" : "menu";
    public static theme: string = window.USING_DARK_THEME ? "dark" : "light";
    public static loading: boolean = false;
    public static mod: boolean = true;
    public static admin: boolean = true;
    public static owner: boolean = true;
    public static participant: boolean = true;
    public static canvasWriter: boolean = true;
    public static accessCode: string = "";
    public static meta: any = null;
    private static events: { [key: string]: Array<(...args: any[]) => void> } = {};
    public static editorActive: boolean = false;
    public static joinedCall: CallsController = null;
    // public static callDate: number = 0;
    public static savedStreams: any = {};
    public static players: any = [];
    public static playersMap: any = {};
    public static usersCount: number = 0;

    public static recording: boolean = false;
    public static recordingUser: string = "";
    public static subtitlesActive: boolean = false;
    public static unreadChats: string[] = [];
    public static currentChat: string = "";
    public static isChatMinimized: boolean = false;
    public static isChatHidden: boolean = false;
    public static startRecordingUser: boolean = false;

    public static volume: number = 1;

    /** Pending room join info for RoomAccessModal to use after authentication */
    public static pendingJoinRoomId: string = "";
    public static pendingJoinAccessCode: string = "";
    public static pendingJoinRedirectPath: string = "";

    public static init() {
        AppEvents.AddEventListener("room", RoomController.onRoomChanged.bind(this));
        AppEvents.AddEventListener("room-leave", RoomController.onRoomLeave.bind(this));
        AppEvents.AddEventListener("room-roles", RoomController.onRoomRoles.bind(this));
        AppEvents.AddEventListener("room-menu-updated", RoomController.onMenuUpdated.bind(this));
        AppEvents.AddEventListener("room-join-request", RoomController.onJoinRequest.bind(this));
        AppEvents.AddEventListener("room-section-updated", RoomController.onSectionUpdated.bind(this));
        AppEvents.AddEventListener("room-section-deleted", RoomController.onSectionDeleted.bind(this));
        AppEvents.AddEventListener("chat", RoomController.onChat.bind(this));
        AppEvents.AddEventListener("chat-error", RoomController.onChatError.bind(this));
        AppEvents.AddEventListener("chat-delete", RoomController.onChatDelete.bind(this));
        AppEvents.AddEventListener("streaming-start", RoomController.onStreamingStart.bind(this));
        AppEvents.AddEventListener("streaming-end", RoomController.onStreamingEnd.bind(this));
        AppEvents.AddEventListener("auth-status-changed", RoomController.onAuthChanged.bind(this));
        AppEvents.AddEventListener("call-users-update", RoomController.onUpdateUsers.bind(this));

        setInterval(RoomController.sendRoomActivity.bind(this), 30000);
    }

    /* Events */

    public static addEventListener(event: string, listener: (...args: any[]) => void) {
        if (!RoomController.events[event]) {
            RoomController.events[event] = [];
        }
        RoomController.events[event].push(listener);
    }

    public static on(event: string, listener: (...args: any[]) => void) {
        RoomController.addEventListener(event, listener);
    }

    public static removeEventListener(event: string, listener: (...args: any[]) => void) {
        if (!RoomController.events[event]) {
            return;
        }
        const i = RoomController.events[event].indexOf(listener);
        if (i >= 0) {
            RoomController.events[event].splice(i, 1);
            if (RoomController.events[event].length === 0) {
                delete RoomController.events[event];
            }
        }
    }

    public static emit(event: string, ...args: any[]) {
        if (!RoomController.events[event]) {
            return;
        }
        for (const listener of [...RoomController.events[event]]) {
            try {
                listener(...args);
            } catch (e) {
                console.error("[RoomController] Error in listener for event '" + event + "':", e);
            }
        }
    }

    /* Actions */

    private static emitCurrentRoles() {
        RoomController.emit("room-roles", RoomController.room, {
            admin: RoomController.admin,
            mod: RoomController.mod,
            owner: RoomController.owner,
            participant: RoomController.participant,
            canvasWriter: RoomController.canvasWriter,
            accessCode: RoomController.accessCode,
        });
    }

    public static joinRoom(roomId: string, accessCode?: string) {
        if (RoomController.room === roomId) {
            if (RoomController.meta) {
                RoomController.emit("loading", false);
                RoomController.emit("room-meta", roomId, RoomController.meta);
                RoomController.emitCurrentRoles();
            }
            return;
        }

        if (window.ROOM_FORCED && roomId !== window.ROOM_FORCED) {
            location.replace(`${window.location.protocol}//${window.location.host}/room/${encodeURIComponent(roomId)}`);
            return;
        }

        if (!roomId) {
            RoomController.leaveRoom();
        }

        RoomController.loading = true;
        RoomController.emit("loading", RoomController.loading);

        Request.Abort("room-meta-load");

        Request.Do(
            ApiRoom.GetRoomRoomid(
                roomId,
                accessCode
                    ? {
                          "x-room-version-code": accessCode,
                      }
                    : {},
            ),
        )
            .onSuccess((response: any) => {
                if (RoomController.room !== roomId) {
                    RoomController.resetLayout();
                }
                RoomController.room = roomId;
                RoomController.accessCode = accessCode || "";
                RoomController.loading = false;
                RoomController.emit("loading", RoomController.loading);
                RoomController.emit("room-meta", roomId, response);
                RoomController.meta = response;

                abortNamedApiRequest("room-meta-update"); // Abortar cualquier actualización previa

                if (CallsController.joinedCall) {
                    // Keep WebSocket on the call room; sync after the call ends
                    RoomController.pendingWebSocketRoom = roomId;
                } else {
                    WebSocketController.joinRoom(roomId); // Unirse a la sala para escuchar eventos
                }
            })
            .onRequestError((err, handleErr) => {
                RoomController.loading = false;
                RoomController.emit("loading", RoomController.loading);
                handleErr(err, {
                    unauthorized: () => {
                        // Store pending join info so modal can complete the flow
                        RoomController.pendingJoinRoomId = roomId;
                        RoomController.pendingJoinAccessCode = accessCode || "";
                        RoomController.emit("room-requires-login");
                    },
                    notFound: () => {
                        AppEvents.Emit(
                            "msg-modal",
                            $t("Room not found"),
                            $t("The room you tried to join was not found. This means it was probably deleted."),
                        );
                        RoomController.emit("room-deleted", roomId);
                        RoomController.emit("show-all-rooms");
                    },
                    forbidden: () => {
                        // If we have an access code, try the join-with-code endpoint first
                        if (accessCode) {
                            RoomController.tryJoinWithCode(roomId, accessCode);
                        } else {
                            RoomController.emit("room-cant-join", roomId);
                            if (window.ROOM_FORCED) {
                                RoomController.emit("room-access-denied");
                            } else {
                                RoomController.emit("show-all-rooms");
                                RoomController.emit("open-create-room-modal", roomId);
                            }
                        }
                    },
                    temporalError: () => {
                        // Error del servidor, reintentar
                        RoomController.loading = true;
                        RoomController.emit("loading", RoomController.loading);
                        setTimeout(() => {
                            RoomController.joinRoom(roomId, accessCode);
                        }, 1000);
                    },
                });
            })
            .onUnexpectedError((err) => {
                console.error("Unexpected error:", err);
            });
    }

    public static leaveRoom() {
        if (!RoomController.room) {
            return; // Nothing to leave
        }

        RoomController.room = "";
        RoomController.accessCode = "";
        RoomController.section = "";

        RoomController.resetLayout();

        // Abort any join requests
        abortNamedApiRequest("room-meta-load");
        abortNamedApiRequest("room-meta-load");
        abortNamedApiRequest("room-meta-update");

        RoomController.meta = null;
        RoomController.emit("room-meta", "", null); // Remove all data

        if (CallsController.joinedCall) {
            // Keep WebSocket on the call room; clear pending room sync
            RoomController.pendingWebSocketRoom = "";
        } else {
            WebSocketController.leaveRoom(); // Leave room via socket
        }

        RoomController.loading = false;
        RoomController.emit("loading", RoomController.loading);
    }

    /**
     * Try to join a room using an access code (private-code mode).
     * If successful, retries joinRoom to load room data.
     */
    public static tryJoinWithCode(roomId: string, code: string) {
        RoomController.loading = true;
        RoomController.emit("loading", RoomController.loading);

        Request.Do(ApiRoom.GetRoomsJoinRoom(roomId, { code: code }))
            .onSuccess((response) => {
                if (response.canJoin) {
                    // Code accepted, now retry loading the room
                    RoomController.loading = false;
                    RoomController.emit("loading", RoomController.loading);
                    // Reset room so joinRoom doesn't early-return
                    RoomController.room = "";
                    RoomController.joinRoom(roomId, code);
                } else if (response.banned) {
                    RoomController.loading = false;
                    RoomController.emit("loading", RoomController.loading);
                    AppEvents.Emit("msg-modal", $t("Access denied"), $t("You are banned from this room."));
                    RoomController.emit("room-cant-join", roomId);
                    RoomController.emit("show-all-rooms");
                } else if (response.canRequest) {
                    RoomController.loading = false;
                    RoomController.emit("loading", RoomController.loading);
                    // Room requires moderator approval
                    RoomController.emit("room-cant-join", roomId);
                    RoomController.emit("show-all-rooms");
                    RoomController.emit("open-create-room-modal", roomId);
                } else {
                    RoomController.loading = false;
                    RoomController.emit("loading", RoomController.loading);
                    RoomController.emit("room-cant-join", roomId);
                    if (window.ROOM_FORCED) {
                        RoomController.emit("room-access-denied");
                    } else {
                        RoomController.emit("show-all-rooms");
                        RoomController.emit("open-create-room-modal", roomId);
                    }
                }
            })
            .onRequestError((err, handleErr) => {
                RoomController.loading = false;
                RoomController.emit("loading", RoomController.loading);
                handleErr(err, {
                    notFound: () => {
                        AppEvents.Emit("msg-modal", $t("Room not found"), $t("The room you tried to join was not found. This means it was probably deleted."));
                        RoomController.emit("room-deleted", roomId);
                        RoomController.emit("show-all-rooms");
                    },
                    unauthorized: () => {
                        // Not authenticated - show login modal
                        RoomController.pendingJoinRoomId = roomId;
                        RoomController.pendingJoinAccessCode = code;
                        RoomController.emit("room-requires-login");
                    },
                    temporalError: () => {
                        RoomController.loading = true;
                        RoomController.emit("loading", RoomController.loading);
                        setTimeout(() => {
                            RoomController.tryJoinWithCode(roomId, code);
                        }, 1000);
                    },
                });
            })
            .onUnexpectedError((err) => {
                RoomController.loading = false;
                RoomController.emit("loading", RoomController.loading);
                console.error("Unexpected error joining with code:", err);
                RoomController.emit("room-cant-join", roomId);
                RoomController.emit("show-all-rooms");
            });
    }

    /* Event handlers */

    private static onAuthChanged() {
        // Don't leave the room during a session transition (SetSession temporarily clears UID)
        if (!AuthController.UID && !AuthController.Session && !AuthController.anonToken) {
            RoomController.leaveRoom();
        }
    }

    private static onRoomChanged(event: any) {
        // Ignore a stale empty-room WS event after we have already rejoined
        if (!event.room && RoomController.room) {
            return;
        }
        if (RoomController.room !== event.room) {
            RoomController.resetLayout();
        }
        RoomController.room = event.room;
        RoomController.mod = !!event.mod;
        RoomController.admin = !!event.admin;
        RoomController.owner = !!event.owner;
        RoomController.participant = !!event.participant;
        RoomController.canvasWriter = !!event.canvasWriter;
        RoomController.accessCode = event.accessCode;

        RoomController.emitCurrentRoles();

        WebSocketController.registerUserActivity(RoomController.room, true);
    }

    private static onRoomLeave(event: any) {
        if (event.room && RoomController.room && event.room !== RoomController.room) {
            return;
        }

        RoomController.room = "";
        RoomController.accessCode = "";
        RoomController.section = "";
        RoomController.mod = false;
        RoomController.admin = false;
        RoomController.owner = false;
        RoomController.participant = false;
        RoomController.canvasWriter = false;

        RoomController.emitCurrentRoles();
        RoomController.emit("room-meta", RoomController.room, null);

        RoomController.meta = null;

        switch (event.reason) {
            case "NOT_FOUND":
                if (window.ROOM_FORCED) {
                    RoomController.emit("room-access-denied");
                } else {
                    AppEvents.Emit(
                        "msg-modal",
                        $t("Room not found"),
                        $t("The room you tried to join was not found. This means it was probably deleted."),
                    );
                }
                break;
            case "DENIED":
                if (window.ROOM_FORCED) {
                    RoomController.emit("room-access-denied");
                } else {
                    if (AuthController.isAnon && AuthController.isAnon() && RoomController.accessCode) {
                        break;
                    }else{
                        AppEvents.Emit(
                            "msg-modal",
                            $t("Cannot join the room"),
                            $t("You lack the required permission to join the room."),
                        );
                    }
                }
                break;
            case "KICKED":
                if (window.ROOM_FORCED) {
                    //App.showRoomKicked();
                } else {
                    RoomController.emit("room-kick");
                }
                break;
        }
    }

    private static onRoomRoles(event: any) {
        if (RoomController.room !== event.room) return;

        RoomController.mod = !!event.mod;
        RoomController.admin = !!event.admin;
        RoomController.owner = !!event.owner;
        RoomController.participant = !!event.participant;
        RoomController.canvasWriter = !!event.canvasWriter;

        RoomController.emit("room-roles", RoomController.room, {
            admin: RoomController.admin,
            mod: RoomController.mod,
            owner: RoomController.owner,
            participant: RoomController.participant,
            canvasWriter: RoomController.canvasWriter,
        });
    }

    private static onMenuUpdated(event: any) {
        if (RoomController.room !== event.room) return;

        const roomId = event.room;
        Request.Do(
            ApiRoom.GetRoomRoomid(
                roomId,
                RoomController.accessCode
                    ? {
                          "x-room-version-code": RoomController.accessCode,
                      }
                    : {},
            ),
        )
            .onSuccess((response: any) => {
                if (roomId === RoomController.room) {
                    RoomController.emit("room-meta", RoomController.room, response);
                    RoomController.meta = response;
                }
            })
            .onRequestError((xhr: any) => {
                console.error("Error al obtener la información de la sala:", xhr);
            })
            .onUnexpectedError((err) => {
                console.error("Unexpected error:", err);
            });
    }

    private static onJoinRequest(event: any) {
        if (RoomController.room !== event.room) return;
        RoomController.emit(event.event, event);
    }

    private static onSectionUpdated(event: any) {
        if (RoomController.room !== event.room) return;
        if (RoomController.editorActive) return;
        RoomController.emit(event.event, event);
    }

    private static onSectionDeleted(event: any) {
        if (RoomController.room !== event.room) return;
        RoomController.emit(event.event, event);
    }

    private static onChat(event: any) {
        // Allow events for the page room OR the active call room
        // (the user may have navigated away from RoomPage while still in a call)
        if (RoomController.room !== event.room &&
            !(CallsController.joinedCall && CallsController.room === event.room)) return;
        RoomController.emit(event.event, event);
    }

    private static onChatError(event: any) {
        if (RoomController.room !== event.room &&
            !(CallsController.joinedCall && CallsController.room === event.room)) return;
        RoomController.emit(event.event, event);
    }

    private static onChatDelete(event: any) {
        if (RoomController.room !== event.room &&
            !(CallsController.joinedCall && CallsController.room === event.room)) return;
        RoomController.emit(event.event, event);
    }

    private static onStreamingStart(event: any) {
        if (RoomController.room !== event.room) return;
        RoomController.emit(event.event, event);
    }

    private static onStreamingEnd(event: any) {
        if (RoomController.room !== event.room) return;
        RoomController.emit(event.event, event);
    }

    public static joinSection(mode: string, sid: string) {
        RoomController.sectionMode = mode;
        RoomController.section = sid;
        RoomController.emit("section-change", mode, sid);
    }

    public static resetLayout() {
        RoomController.sectionModeAlt = "section";
        RoomController.sectionAlt = "";
        RoomController.emit("section-change-alt", "section", "");
        RoomController.layout = "single";
        RoomController.emit("layout-change", RoomController.layout);
        RoomController.multiStreamingSelected = "";
        RoomController.isMultiStreaming = false;
        RoomController.multiStreamingList = [];
    }

    public static changeSectionAlt(mode: string, sid: string, layout: string) {
        if (layout === "single") {
            RoomController.joinSection(mode, sid);
            RoomController.sectionModeAlt = "section";
            RoomController.sectionAlt = "";
            RoomController.emit("section-change-alt", "section", "");
        } else if (mode !== "multistream" && RoomController.isMultiStreaming) {
            RoomController.isMultiStreaming = false;
            RoomController.sectionModeAlt = mode;
            RoomController.sectionAlt = sid;
            RoomController.emit("section-change-alt", mode, sid);
            RoomController.joinSection("section", RoomController.multiStreamingSelected);
        } else {
            RoomController.sectionModeAlt = mode;
            RoomController.sectionAlt = sid;
            RoomController.emit("section-change-alt", mode, sid);
        }

        RoomController.layout = layout;
        RoomController.emit("layout-change", layout);
    }

    public static changeFocus(id: string) {
        RoomController.focus = id;
        RoomController.emit("tab-focus", id);
    }

    public static showStreamingSectionInList(id: string) {
        RoomController.emit("streaming-list-add", id);
    }

    public static updateRoomAccessList() {
        RoomController.emit("update-room-access-list");
    }

    public static onMultiStreamingShow() {
        if (RoomController.sectionModeAlt !== "multistream") {
            RoomController.sectionModeAlt = "multistream";
            RoomController.sectionAlt = "";
            RoomController.emit("section-change-alt", RoomController.sectionModeAlt, RoomController.sectionAlt);
            if (["right", "left", "rightmin", "leftmin"].indexOf(RoomController.layout) === -1) {
                RoomController.layout = "rightmin";
                RoomController.emit("layout-change", RoomController.layout);
            }
        }
        RoomController.isMultiStreaming = true;
        RoomController.emit("multi-streaming-status-change", true);
    }

    public static onMultiStreamingHide() {
        if (RoomController.sectionModeAlt === "multistream") {
            RoomController.sectionModeAlt = "section";
            RoomController.sectionAlt = "";
            RoomController.emit("section-change-alt", "section", "");
            RoomController.layout = "single";
            RoomController.emit("layout-change", RoomController.layout);
        }
        RoomController.multiStreamingSelected = "";
        RoomController.isMultiStreaming = false;
        RoomController.multiStreamingList = [];
        RoomController.emit("multi-streaming-status-change", true);
        RoomController.emit("multi-streaming-list-change", []);
    }

    public static onMultiStreamingList(list: MultiStreamingItem[]) {
        RoomController.multiStreamingList = list;
        RoomController.emit("multi-streaming-list-change", list);
    }

    public static onMultiStreamingMainChange(sid: string) {
        RoomController.multiStreamingSelected = sid;
        RoomController.emit("multi-streaming-section-change", sid);
    }

    public static setIsMinimized(val: boolean) {
        RoomController.isChatMinimized = val;
    }

    public static setIsHidden(val: boolean) {
        RoomController.isChatHidden = val;
    }

    public static setActiveEditor(b: boolean) {
        RoomController.editorActive = b;
    }

    public static hasStreaming(meta: any): boolean {
        if (!meta || !meta.sections) {
            return false;
        }
        for (const section of meta.sections) {
            if (section.type === "streaming" && section.visible) {
                return true;
            }
        }

        return false;
    }

    public static sendRoomActivity() {
        if (!RoomController.room) {
            return;
        }

        WebSocketController.registerUserActivity(RoomController.room, true);
    }

    public static changeTheme(isDark: boolean) {
        RoomController.theme = isDark ? "dark" : "light";
        RoomController.emit("page-theme", RoomController.theme);
    }

    public static onOpenCall(selfUser, streams) {
        this.savedStreams = streams;
        this.players.push(selfUser);
        this.playersMap[selfUser.uid] = selfUser;
    }

    public static onLeaveCall() {
        this.savedStreams = {};
        this.playersMap = {};
        this.players = [];
    }

    public static onUserLeave(id) {
        const player = this.players.find((player) => player.uid === id);
        if (player && player.streamid) {
            delete this.savedStreams[player.streamid];
        }
        this.players = this.players.filter((player) => player.uid !== id);
        delete this.playersMap[id];
    }

    public static onUserJoin(id, user: any) {
        this.playersMap[id] = user;
        this.savedStreams[user.streamid] = user.stream;
    }
    public static onUpdateUsers(event: any) {
        this.usersCount = event.users;
        RoomController.emit("call-users-update", event);
    }

    public static askRecordingOnJoin(user, adminRecord) {
        this.recordingUser = adminRecord;
        CallsController.emit("ask-recording", user);
    }

    public static handleRecordingConfirm() {
        RoomController.emit("join-recording-call");
    }
    public static startRecord(rid, user, recordId) {
        if (this.room !== rid) {
            console.error("Room ID does not match");
            return;
        }
        this.recording = true;
        this.recordingUser = user;
        CallsController.startRecording(user, recordId);
        RoomController.emit("start-record", user);
    }

    public static stopRecord() {
        this.recordingUser = "";
        this.recording = false;
        RoomController.emit("stop-record");
    }
    public static stopRecordingUser() {
        RoomController.emit("stop-recording-user");
    }
    public static updateVolume(volume: number) {
        RoomController.volume = volume;
        RoomController.emit("volume-changed", volume);
    }

    /**
     * Shared subtitle toggle state for all call UIs.
     * Emits an event so mounted views stay in sync instantly.
     */
    public static setSubtitlesActive(active: boolean, source: string = "") {
        RoomController.subtitlesActive = !!active;
        RoomController.emit("subtitles-changed", {
            active: RoomController.subtitlesActive,
            source,
        });

        // Translation is a paid/long-running backend resource. Keep its
        // lifetime aligned with the shared captions preference instead of
        // starting it for every call and leaving Azure running while captions
        // are hidden.
        if (RoomController.subtitlesActive) {
            CallsController.startTranslation();
        } else {
            CallsController.stopTranslation();
        }
    }

    public static setUserVolume(uid: string, volume: number) {
        if (!RoomController.playersMap[uid]) {
            console.warn(`User with UID ${uid} not found in players map.`);
            return;
        }
        RoomController.playersMap[uid].volume = volume;
        RoomController.emit("user-volume-changed", uid, volume);
    }

    public static onScreenShareStart(uid: string, userInfo: { name: string; image: string }) {
        this.playersMap[uid] = userInfo;
        RoomController.emit("screen-share-start", uid, userInfo);
    }

    public static onScreenShareEnd(uid: string) {
        delete this.playersMap[uid];
        RoomController.emit("screen-share-end", uid);
    }

    public static onScreenShareStreamReceived(id: string, stream: MediaStream) {
        this.savedStreams[id] = stream;
        RoomController.emit("screen-share-stream", id, stream);
    }
}
